import type {
  CountryDistributionItem,
  DashboardStats,
  FundraisingResponse,
  LatestDelivery,
  ListResponse,
  MonthlyAddedItem,
  NotificationResponse,
  ProfileCategoryCountsResponse,
  ProfileResponse,
  SourceSplitItem,
  TopCompatibleLP,
} from '@shared/schemas';

import type { DashboardScope } from '../domain/scope';

/** One dashboard card's slice of state: its payload + its own load lifecycle. */
export interface Panel<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

/**
 * Redux state for the dashboard. Independent panels (one per endpoint)
 * so a failing fetch marks only its own card while the others render normally
 * (the legacy page's `.catch(() => {})` resilience, but with the error finally
 * recorded). Derived chart data (LP scores, source-split slices, cumulative
 * activity, KPI tiles) is computed by memoised selectors over these raw
 * payloads, never stored.
 */
export interface DashboardState {
  notifications: Panel<NotificationResponse[]>;
  lists: Panel<ListResponse[]>;
  countries: Panel<CountryDistributionItem[]>;
  topLps: Panel<TopCompatibleLP[]>;
  trackedLps: Panel<ProfileResponse[]>;
  stats: Panel<DashboardStats | null>;
  latestFundraising: Panel<FundraisingResponse | null>;
  sourceSplit: Panel<SourceSplitItem[]>;
  categoryCounts: Panel<ProfileCategoryCountsResponse | null>;
  monthlyAdded: Panel<MonthlyAddedItem[]>;
  latestDelivery: Panel<LatestDelivery | null>;
}

/** The panels plus the cross-filter they are read under. */
export interface DashboardSliceState extends DashboardState {
  /** What the user clicked to narrow the page (type, country, month). */
  scope: DashboardScope;
  /** The scoped panels are being re-read for a new scope: the page keeps its figures, dimmed. */
  refreshing: boolean;
}

/** The panels a scope narrows; re-read whenever it changes. */
export const SCOPED_PANELS = [
  'stats',
  'sourceSplit',
  'categoryCounts',
  'countries',
  'monthlyAdded',
] as const satisfies readonly (keyof DashboardState)[];

/**
 * The panels the dashboard shows, hence loads. Top Compatible Profiles,
 * Profiles & Lists Overview and Recent Activity are hidden for now (#637), and
 * so is the fundraising line (the client tool is not a fundraising tracker yet). Hidden panels:
 * their state, reducers, reads and views are kept so they can come back, but
 * nothing loads them; add their key here (and their read to the saga) to
 * bring one back.
 */
export const LOADED_PANELS = [
  'latestDelivery',
  'countries',
  'trackedLps',
  'stats',
  'sourceSplit',
  'categoryCounts',
  'monthlyAdded',
] as const satisfies readonly (keyof DashboardState)[];

const panel = <T>(data: T): Panel<T> => ({ data, loading: false, error: null });

export const initialState: DashboardSliceState = {
  scope: {},
  refreshing: false,
  notifications: panel<NotificationResponse[]>([]),
  lists: panel<ListResponse[]>([]),
  countries: panel<CountryDistributionItem[]>([]),
  topLps: panel<TopCompatibleLP[]>([]),
  trackedLps: panel<ProfileResponse[]>([]),
  stats: panel<DashboardStats | null>(null),
  latestFundraising: panel<FundraisingResponse | null>(null),
  sourceSplit: panel<SourceSplitItem[]>([]),
  categoryCounts: panel<ProfileCategoryCountsResponse | null>(null),
  monthlyAdded: panel<MonthlyAddedItem[]>([]),
  latestDelivery: panel<LatestDelivery | null>(null),
};
